/** Settlements that were never formed, read as a sentence (FIND-49).
 *
 * A third state beside abandoned and failed: never queued, nothing will retry
 * it, and the stop does not come again. So the card is not "how many failed",
 * it is "how many sessions can no longer be billed without a person".
 *
 * The reasons are shown rather than summed away, because they are acted on
 * differently: a missing reading is completed from the OCPP Server's own
 * record of the transaction, and a stop below its start is a replaced meter,
 * a swapped pair or a reading from another transaction.
 */

export type IncompleteCounts = Record<string, number>

/** What the service writes, in words an operator reads. Anything else is
 *  shown as it arrives: a reason this console has not heard of is still a
 *  settlement nobody will bill, and hiding it behind "other" would lose the
 *  one fact a person needs to act. */
const REASONS: Record<string, string> = {
  'stop below start': 'a stop below its start',
  meterStartWh: 'no start reading',
  meterStopWh: 'no stop reading',
  'meterStartWh, meterStopWh': 'neither reading',
  'not recorded': 'no reason recorded',
}

export function incompleteTotal(counts: IncompleteCounts | undefined): number {
  return Object.values(counts ?? {}).reduce((sum, n) => sum + n, 0)
}

export function incompleteCaption(counts: IncompleteCounts | undefined): string {
  const entries = Object.entries(counts ?? {}).filter(([, n]) => n > 0)
  if (entries.length === 0) {
    return 'Every session that ended had the readings to bill it'
  }
  // Largest first, because the one to chase is the one happening most, and
  // by reason after that so two equal counts do not swap places between
  // renders and read as a page that cannot make up its mind.
  entries.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  const parts = entries.map(([reason, n]) => `${n} with ${REASONS[reason] ?? reason}`)
  return `${parts.join(', ')}. Each needs a person`
}
